<template>
  <v-container v-if="$can('browse', 'Base')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Bases
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'Base')"
          color="primary"
          class="ma-0"
          @click="openBaseEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-autocomplete
          v-model="filtersDirty.country_id"
          solo
          label="Country"
          :items="charteringCountries.data"
          :loading="charteringCountries.loading"
          item-text="name"
          item-value="id"
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="bases.meta.from"
          :to="bases.meta.to"
          :total="bases.meta.total"
          :last-page="bases.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="bases.data"
          :loading="bases.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="bases.meta.total"
          hide-default-footer
          must-sort
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <base-row
              :base="item"
              @click.native="openBaseEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  color="default"
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no bases at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  class="ma-0"
                  small
                  @click="openBaseEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="bases.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <base-edit-dialog
      :visible.sync="baseEditDialog"
      :base="baseDirty"
      @base:created="baseCreated"
      @base:updated="baseUpdated"
      @base:deleted="baseDeleted"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import { mapGetters } from 'vuex'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import Base from '@/models/Base'
  import BaseRow from '@/components/business/bases/BaseRow'
  import BaseEditDialog from '@/components/business/bases/BaseEditDialog'
  import TableFooter from '@/components/helpers/TableFooter'

  export default {
    name: 'BasesIndex',
    components: {
      BaseRow,
      BaseEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        bases: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Address', value: 'address', sortable: false },
          { text: 'Country', value: 'country_id', sortable: false }
        ],
        baseDirty: null,
        baseEditDialog: false,
        filtersClean: Base.emptyFilters,
        filtersDirty: cloneDeep(Base.emptyFilters)
      }
    },
    computed: {
      ...mapGetters('settings', ['charteringCountries'])
    },
    created() {
      this.fetchBases()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchBases()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchBases() {
        this.bases.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await Base.api.fetch(queryParams)
          this.bases.data = result.data
          this.bases.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.bases.loading = false
        }
      },
      openBaseEditDialog(base = undefined) {
        this.baseDirty = cloneDeep(base)
        this.baseEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(Base.emptyFilters)
      },
      baseCreated(base) {
        this.bases.data.unshift(base)
      },
      baseUpdated(base) {
        const index = this.bases.data.findIndex(b => b.id === base.id)

        this.bases.data.splice(index, 1, base)
      },
      baseDeleted(baseId) {
        const index = this.bases.data.findIndex(b => b.id === baseId)

        this.bases.data.splice(index, 1)
      }
    }
  }
</script>
